import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Tab,
  useTheme,
  Tooltip,
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { UPDATE_AUDIT } from "../../../../apis/settingsApis/audit-apis/auditApis";
import { GET_USER_DETAILS } from "../../../../apis/dashboard-apis/dashboardDetailApi";
import SummaryTab from "../components/Tabs/SummaryTab";
import AuditPlanTab from "../components/Tabs/AuditPlanTab";
import ControlsTab from "../components/Tabs/ControlsTab";
import EvidenceRequestsTab from "../components/Tabs/EvidenceRequestsTab";
import AccessAssessmentsTab from "../components/Tabs/AccessAssessmentsTab";
import RiskAssessmentsTab from "../components/Tabs/RiskAssessmentsTab";
import AuditDetailsCard from "../features/audit-details/AuditDetailsCard";
import CompleteAuditDialog from "../components/Dialogs/CompleteAuditDialog";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import { useAuditDetailsHandlers } from "../hooks/useAuditDetailsHandlers";
import { useAuditDetails } from "../hooks/useAuditDetails";
import { formatDateForInput } from "../../../../utils/dateUtils";

const TAB_VALUE_TO_KEY = {
  "0": "summary",
  "1": "audit-plan",
  "2": "controls",
  "3": "evidence-requests",
  "4": "access-assessments",
  "5": "risk-assessments",
};

const TAB_KEY_TO_VALUE = Object.fromEntries(
  Object.entries(TAB_VALUE_TO_KEY).map(([value, key]) => [key, value])
);

const AuditDetails = ({ isAuditorView = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [tab, setTab] = useState("0");
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabKeyFromUrl = searchParams.get("tab");

    if (tabKeyFromUrl && TAB_KEY_TO_VALUE[tabKeyFromUrl]) {
      const tabValue = TAB_KEY_TO_VALUE[tabKeyFromUrl];
      if (tabValue !== tab) {
        setTab(tabValue);
      }
      return;
    }

    if (location.state?.activeTab !== undefined) {
      const fromState = String(location.state.activeTab);
      if (fromState !== tab) {
        setTab(fromState);
      }
    }
  }, [location.search, location.state, tab]);

  const {
    audit,
    processedAudit,
    auditLoading,
    refetchAudit,
    auditorPickerOptions,
  } = useAuditDetails(id);

  const { data: userDetailsData } = useQuery(GET_USER_DETAILS);
  const userDetails = userDetailsData?.getAccountDetails?.userDetails || {};

  const [updateAudit, { loading: updating }] = useMutation(UPDATE_AUDIT);

  const handlers = useAuditDetailsHandlers(
    processedAudit,
    updateAudit,
    refetchAudit,
    setSnackbarInfo
  );

  // For external audits the inline picker should only show external auditors
  const filteredAuditorOptions = useMemo(() => {
    if (processedAudit?.audit_type === "external") {
      return auditorPickerOptions.filter((opt) => !opt.is_internal);
    }
    return auditorPickerOptions;
  }, [auditorPickerOptions, processedAudit?.audit_type]);

  const auditEndDatePassed = useMemo(() => {
    if (!processedAudit?.audit_end_date) return false;
    const endDate = formatDateForInput(processedAudit.audit_end_date);
    if (!endDate) return false;
    const today = new Date();
    return endDate <= formatDateForInput(today);
  }, [processedAudit]);

  // Rule 4: check if the current user is in the audit's auditors list.
  // Check both employee_data_id (internal) and user_id (external) — backend may
  // return either depending on user type, so we match against both.
  const isAssignedAuditor = useMemo(() => {
    if (!processedAudit?.auditors?.length) return false;
    const candidateIds = [userDetails.employee_data_id, userDetails.user_id].filter(Boolean);
    if (!candidateIds.length) return false;
    return processedAudit.auditors.some((a) =>
      candidateIds.some((cid) => String(a.id) === String(cid))
    );
  }, [processedAudit, userDetails]);

  // Complete button: visible only when user is an assigned auditor and audit is not completed.
  // Enabled only once audit end date has also passed.
  const canCompleteAudit = processedAudit?.audit_status !== "completed" && isAssignedAuditor;
  const canCompleteAuditNow = canCompleteAudit && auditEndDatePassed;

  const handleCompleteAudit = async () => {
    try {
      const result = await updateAudit({
        variables: {
          input: [
            {
              id: id,
              audit_status: "completed",
            },
          ],
        },
      });

      if (result.data?.updateAudit?.success) {
        const newAuditId = result.data?.updateAudit?.data?.[0]?.id;

        setCompleteDialogOpen(false);
        setSnackbarInfo({
          message: "Audit completed successfully!",
          vertical: "top",
          horizontal: "right",
        });

        if (newAuditId && newAuditId !== id) {
          const basePath = isAuditorView ? "/auditor/audits" : "/admin/audits";
          navigate(`${basePath}/${newAuditId}`);
        } else {
          await refetchAudit();
        }
      } else {
        const specificError = result.data?.updateAudit?.data?.[0]?.error;
        const errorMessage = specificError || result.data?.updateAudit?.message || "Failed to complete audit";
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error completing audit:", error);
      setSnackbarInfo({
        message: error.message || "Failed to complete audit",
        vertical: "top",
        horizontal: "right",
      });
      throw error;
    }
  };

  if (auditLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!processedAudit) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(isAuditorView ? "/auditor/audits" : "/admin/audits")}
          sx={{
            mb: 2,
            color: theme.customColors?.text?.secondary,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Back to Audits Page
        </Button>
        <Typography variant="h5" color="error">
          Audit not found
        </Typography>
      </Box>
    );
  }

  const completeTooltip = !isAssignedAuditor
    ? "Only an assigned auditor can mark this audit as completed"
    : !auditEndDatePassed
    ? "Audit can only be completed after the audit period end date"
    : "";

  return (
    <Box sx={{ padding: { xs: 2 }, minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(isAuditorView ? "/auditor/audits" : "/admin/audits")}
          sx={{
            color: theme.customColors?.text?.secondary,
            textTransform: "none",
            fontWeight: 500,
            "&:hover": { bgcolor: theme.customColors?.background?.light },
          }}
        >
          Back to Audits Page
        </Button>

        {/* Complete button: only visible to users who are assigned auditors for this audit */}
        {canCompleteAudit && (
          <Tooltip title={completeTooltip} arrow>
            <span>
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={() => setCompleteDialogOpen(true)}
                disabled={!canCompleteAuditNow}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: "8px",
                  bgcolor: theme.customColors.button.primary,
                  color: theme.customColors.button.text,
                  boxShadow: "0 3px 8px rgba(0,0,0,0.18)",
                  "&:hover": {
                    bgcolor: theme.palette._blue?.dark || theme.customColors.button.primary,
                    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.35)",
                  },
                  "&:disabled": {
                    bgcolor: "#D1D5DB",
                    color: "#9CA3AF",
                    boxShadow: "none",
                  },
                }}
              >
                Complete Audit
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* Audit Details Card */}
      <AuditDetailsCard
        processedAudit={processedAudit}
        handlers={handlers}
        updating={updating}
        isAuditorView={isAuditorView}
        auditorOptions={filteredAuditorOptions}
        detailsExpanded={detailsExpanded}
        onToggleExpanded={() => setDetailsExpanded(!detailsExpanded)}
      />

      <TabContext value={tab}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: theme.customColors?.border?.main,
            mb: 2,
          }}
        >
          <TabList
            onChange={(_, v) => {
              setTab(v);
              const searchParams = new URLSearchParams(location.search);
              const tabKey = TAB_VALUE_TO_KEY[v] || "summary";
              searchParams.set("tab", tabKey);

              const basePath = isAuditorView ? "/auditor/audits" : "/admin/audits";
              navigate(`${basePath}/${id}?${searchParams.toString()}`, {
                replace: true,
              });
            }}
            aria-label="Audit Detail Tabs"
          >
            <Tab label="Summary" value="0" />
            <Tab label="Audit Plan" value="1" />
            <Tab label="Controls" value="2" />
            <Tab label="Evidence Requests" value="3" />
            <Tab label="Access Assessments" value="4" />
            <Tab label="Risk Assessments" value="5" />
          </TabList>
        </Box>

        <TabPanel value="0" sx={{ p: 0 }}>
          <SummaryTab processedAudit={processedAudit} isAuditorView={isAuditorView} />
        </TabPanel>
        <TabPanel value="1" sx={{ p: 0 }}>
          <AuditPlanTab audit={processedAudit} isAuditorView={isAuditorView} />
        </TabPanel>
        <TabPanel value="2" sx={{ p: 0 }}>
          <ControlsTab audit={processedAudit} isAuditorView={isAuditorView} />
        </TabPanel>
        <TabPanel value="3" sx={{ p: 0 }}>
          <EvidenceRequestsTab audit={processedAudit} isAuditorView={isAuditorView} />
        </TabPanel>
        <TabPanel value="4" sx={{ p: 0 }}>
          <AccessAssessmentsTab audit={processedAudit} isAuditorView={isAuditorView} />
        </TabPanel>
        <TabPanel value="5" sx={{ p: 0, minWidth: 0, width: "100%" }}>
          <RiskAssessmentsTab audit={processedAudit} isAuditorView={isAuditorView} />
        </TabPanel>
      </TabContext>

      <CompleteAuditDialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        onConfirm={handleCompleteAudit}
        auditName={processedAudit?.audit_name || ""}
        loading={updating}
      />

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </Box>
  );
};

export default AuditDetails;
